import React, { useEffect, useState, useRef, type TouchEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { Dropdown, Button } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";

import {
  dashboard_logout,
  dashboard_icon,
  dashboard_saved_details_icon,
  dashboard_booking_calendar,
  sidebar_collapse_icon,
  dashboard_logo_kirkos_collapse,
  dashboard_insurance_member_icon,
  dashboard_customer_invoices,
  dashboard_car_status,
  kirkos_logo_white_yellow,
  chat_active,
  registered_contact_logo,
  kirkos_merchant_profile,
} from "../../../assets/images";
import { log_out } from "../../../services/axios.instance";
import axiosInstance from "../../../services/axios.instance";
import { API_URL } from "../../../services/apiConfig";
import { clearBookingValues } from "../../../Redux/BookingData/bookingDataSlice";
import { useDispatch } from "react-redux";
import CloseIcon from '@mui/icons-material/Close';
import { CustomerDetails } from "../../../types";

interface customerDashboardSidebarProps {
  selectedNavToggle: boolean;
  setSelectedNavToggle: (toggle: boolean) => void;
}

const CustomerDashboardSidebar: React.FC<customerDashboardSidebarProps> = ({
  setSelectedNavToggle,
  selectedNavToggle,
}) => {
  const [selectedNav, setSelectedNav] = useState("dashboard");
  const [screenResponsiveSidebar, setScreenResponsiveSidebar] = useState(false);
  const [profile, setProfile] = useState<{ profile_picture?: string, firstName?: string, lastName?: string }>({});

  const dispatch = useDispatch();
  const location = useLocation();

  // Fetch Profile Data
  useQuery<CustomerDetails, Error>({
    queryKey: ["details"],
    queryFn: async () => {
      const response = await axiosInstance.get(API_URL.CUSTOMER_DETAILS);
      setProfile(response?.data?.customer_details || {});
      return response.data;
    },
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setSelectedNavToggle(true);
        setScreenResponsiveSidebar(true);
      } else {
        setSelectedNavToggle(false);
        setScreenResponsiveSidebar(false);
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [setSelectedNavToggle]);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes("rentdeals")) setSelectedNav("saveddetails");
    else if (path.includes("orders")) setSelectedNav("yourOrders");
    else if (path.includes("myaccount")) setSelectedNav("dashboard");
    else if (path.includes("contactus")) setSelectedNav("contactUs");
    else if (path.includes("managemyprofile")) setSelectedNav("managemyprofile");
    else if (path.includes("kirkos-services")) setSelectedNav("kirkosServices");
  }, [location.pathname]);

  const handleSetActiveNavigation = (nav: string): void => {
    setSelectedNav(nav);
    if (screenResponsiveSidebar) {
      setSelectedNavToggle(true);
    }
  };

  const handleLogout = () => {
    log_out();
    dispatch(clearBookingValues());
  };

  const dataNavTop = [
    { label: "Dashboard", icon: dashboard_icon, value: "dashboard", navlink: "/customer/myaccount" },
    { label: "Track Your Order", icon: dashboard_saved_details_icon, value: "saveddetails", navlink: "/customer/rentdeals" },
    { label: "Order History", icon: dashboard_customer_invoices, value: "yourOrders", navlink: "/customer/orders" },
    { label: "Contact Us", icon: registered_contact_logo, value: "contactUs", navlink: "/customer/contactus" },
    // { label: "Kirkos Services", icon: dashboard_car_status, value: "kirkosServices", navlink: "/customer/kirkos-services" }
  ];

  return (
    <aside className={`sidebar-dash-customer ${selectedNavToggle ? "collapsed" : "open"}`}>
      <div className="sidebar-logo-container d-flex align-items-center justify-content-between">
        <Link to="/customer/myaccount">
          <img
            src={selectedNavToggle ? dashboard_logo_kirkos_collapse : kirkos_logo_white_yellow}
            alt="Kirkos"
            className="img-fluid"
          />
        </Link>
        {screenResponsiveSidebar && !selectedNavToggle && (
          <Button
            variant="link"
            className="text-white p-0 d-lg-none"
            onClick={() => setSelectedNavToggle(true)}
          >
            <CloseIcon />
          </Button>
        )}
      </div>

      <nav className="sidebar-nav-list">
        {dataNavTop.map((item) => (
          <div key={item.value} className="sidebar-nav-item">
            <Link
              to={item.navlink}
              onClick={() => handleSetActiveNavigation(item.value)}
              className={`sidebar-link ${selectedNav === item.value ? "active" : ""}`}
            >
              <img src={item.icon} width="20" height="20" alt="" />
              <span className="sidebar-label">{item.label}</span>
            </Link>
          </div>
        ))}
      </nav>

      <div className="sidebar-profile-section">
        <Dropdown drop="up" align={selectedNavToggle ? "end" : "start"}>
          <Dropdown.Toggle as={React.forwardRef<HTMLDivElement, any>(({ onClick }, ref) => (
            <div
              ref={ref}
              className="profile-dropdown-trigger"
              onClick={(e) => { e.preventDefault(); onClick?.(e as any); }}
            >
              <div className="profile-pic-container">
                <img src={profile?.profile_picture || kirkos_merchant_profile} alt="Avatar" />
              </div>
              {!selectedNavToggle && (
                <div className="profile-info">
                  <p className="profile-name">{profile?.firstName || "Guest"} {profile?.lastName || "User"}</p>
                  <p className="profile-role">Customer Account</p>
                </div>
              )}
            </div>
          ))} />

          <Dropdown.Menu className="sidebar-profile-menu w-100">
            <Dropdown.Item as={Link} to="/customer/managemyprofile" className="py-2">
              Manage Profile
            </Dropdown.Item>
            {/* <Dropdown.Divider style={{ borderColor: 'rgba(255,255,255,0.05)' }} /> */}
            <Dropdown.Item onClick={handleLogout} className="py-2 text-danger fs-6" >
              Logout
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </aside>
  );
};

export default CustomerDashboardSidebar;



